import { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

export default function AdminPanel({ adminPassword, onClose }) {
  const [current, setCurrent] = useState({ password_user: "", password_admin: "" });
  const [newUser, setNewUser] = useState("");
  const [newAdmin, setNewAdmin] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/passwords`, { params: { admin_password: adminPassword } })
      .then(r => setCurrent(r.data))
      .catch(() => setError("Impossibile caricare le password. Verifica la connessione."));
  }, [adminPassword]);

  // Focus trap and Escape to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "Tab" && boxRef.current) {
        const focusable = boxRef.current.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    // Focus first interactive element
    const firstFocusable = boxRef.current?.querySelector("button, input");
    firstFocusable?.focus();
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const save = async () => {
    if (!newUser && !newAdmin) return;
    setSaving(true);
    setMsg(""); setError("");
    try {
      await axios.put(`${API}/admin/passwords`, {
        admin_password: adminPassword,
        ...(newUser && { new_password_user: newUser }),
        ...(newAdmin && { new_password_admin: newAdmin }),
      });
      setMsg("Password aggiornate con successo");
      setCurrent(prev => ({
        password_user: newUser || prev.password_user,
        password_admin: newAdmin || prev.password_admin,
      }));
      setNewUser("");
      setNewAdmin("");
    } catch {
      setError("Impossibile salvare le modifiche. Riprova.");
    }
    setSaving(false);
  };

  return (
    <div className="admin-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div
        className="admin-box"
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-dialog-title"
      >
        <div className="admin-header">
          <h3 id="admin-dialog-title">Pannello Amministratore</h3>
          <button className="admin-close" onClick={onClose} aria-label="Chiudi pannello">✕</button>
        </div>

        <div className="admin-section">
          <label htmlFor="admin-current-user">Password Utenti</label>
          <div className="admin-current-row">
            <span>Attuale:</span>
            <code id="admin-current-user">{current.password_user || "…"}</code>
          </div>
          <input
            id="admin-new-user"
            type="password"
            placeholder="Nuova password utenti (lascia vuoto per non modificare)"
            value={newUser}
            onChange={e => setNewUser(e.target.value)}
            aria-label="Nuova password utenti"
          />
        </div>

        <div className="admin-section">
          <label htmlFor="admin-current-admin">Password Amministratore</label>
          <div className="admin-current-row">
            <span>Attuale:</span>
            <code id="admin-current-admin">{current.password_admin || "…"}</code>
          </div>
          <input
            id="admin-new-admin"
            type="password"
            placeholder="Nuova password admin (lascia vuoto per non modificare)"
            value={newAdmin}
            onChange={e => setNewAdmin(e.target.value)}
            aria-label="Nuova password amministratore"
          />
        </div>

        {msg && <div className="admin-msg success" role="status">{msg}</div>}
        {error && <div className="admin-msg error" role="alert">{error}</div>}

        <button
          className="admin-save"
          onClick={save}
          disabled={saving || (!newUser && !newAdmin)}
        >
          {saving ? "Salvataggio..." : "Salva modifiche"}
        </button>
      </div>
    </div>
  );
}
